import { map, pathOr, always, cond, equals, T, propEq, filter, prop, uniqBy, find, includes, trim, flatten } from 'ramda';
import { SystemAuditActivity } from 'src/types/SystemAuditActivity';
import { ApplicationAuditActivity } from 'src/types/ApplicationAuditActivity';
import {
  mkApplicationActivity,
  mkSystemAuditActivity,
  QUERY_CREATE_APPLICATION_ACTIVITIES,
  QUERY_CREATE_SYSTEM_ACTIVITIES
} from 'src/services/grapqlQueries/activitiesQuery';
import { APIResourceValueEvaluator } from '../Configs/apiResourceFields';
import { KIND_LOANAPP_ROLE, SMARTMATCH_TYPE_ADMIN } from 'src/services/constants';
import { checkDeactivatedUponOnEventContext } from './deactivatedUpon';
import { isEmptyOrNil, isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { authenticatedUser } from 'src/utilities/storage';
import { getAccountFromStore } from 'src/hooks/useAccountsHook';
import { GQLQueryBuilder, QUERY_OPERATION_MUTATION, QueryData } from 'src/services/grapqlQueries/queryBuilder';
import { requestGraphQL } from 'src/services/grapqlQueries/useGraphQLQuery';


interface IContext {
  audit_type: string;
  details: any;
  metadata: any;
  conditions?: any;
}

interface IAuditActivityContext {
  payload: () => SystemAuditActivity | ApplicationAuditActivity;
  queryData: () => QueryData | QueryData[];
}

class SystemAuditData {
  systemActivities: any[];
  loanAppId: string;

  constructor(systemActivities: any, loanAppId: string) {
    this.loanAppId = loanAppId;
    this.systemActivities = isNotEmpty(systemActivities) ? filter(propEq('loanapp_id', loanAppId), systemActivities) : [];
  }
  get(resource: string) {
    return find(propEq('resource', resource), this.systemActivities)
  }

  //TO-DO: This needs to be fixed from config saver 
  getByRoleSpecific(activities: any, resourceName: string) {
    const loggedInUserRolesID = authenticatedUser.rolesId.get();
    return filter((activity) => {
      const isResourceMatch = equals(pathOr("", ["resource"], activity), resourceName);
      const isNotAdmin = !includes(SMARTMATCH_TYPE_ADMIN, loggedInUserRolesID);
      return isResourceMatch ? isResourceMatch && isNotAdmin : true;
    }, activities);
  }

  getAllActivityByStatus(resources: string[], status: string) {
    const activity = filter(
      (activity) => includes(pathOr("", ["resource"], activity), resources)
        && equals(pathOr('', ['status'], activity), status),
      uniqBy(prop('message'), this.systemActivities)
    ) || []

    return this.getByRoleSpecific(activity, 'rate_approver_warning')

  }

  hasActivity(activityResource: string) {
    return isNotEmpty(this.get(activityResource))
  }

  isStatus(activityResource: string, status: string) {
    return equals(pathOr('', ['status'], this.get(activityResource)), status)
  }


  //for getting activity for current activity
  getStatus(activity: SystemAuditActivity, status: string) {
    return equals(pathOr('', ['status'], activity), status)
  }
}

/**
SystemAudit class is used to make payload of System audit activity
and request to create new System audit activity
*/
class SystemAudit implements IAuditActivityContext {
  context: any;
  loanAppId: string;
  loanRoleId: string;
  valueEvaluator: APIResourceValueEvaluator;
  constructor(
    context: IContext,
    loanAppId: string,
    loanRoleId: string,
    valueEvaluator: APIResourceValueEvaluator
  ) {
    this.context = context;
    this.loanAppId = loanAppId
    this.loanRoleId = loanRoleId
    this.valueEvaluator = valueEvaluator
  }

  resource() {
    return pathOr('', ['details', 'resource'], this.context);
  }

  entityId() {
    const value = pathOr('', ['details', 'entity_type'], this.context);
    return value ? this.valueEvaluator.replace(value) : this.loanRoleId;
  }

  entityType() {
    return pathOr('', ['details', 'entity_type'], this.context) || KIND_LOANAPP_ROLE;
  }

  action() {
    return pathOr('', ['details', 'action'], this.context);
  }

  actor() {
    const value = pathOr('', ['details', 'actor'], this.context);
    return value ? this.valueEvaluator.replace(value) : value;
  }

  status() {
    return pathOr('', ['details', 'status'], this.context);
  }

  message() {
    const value = pathOr('', ['details', 'message'], this.context);
    return value ? this.valueEvaluator.replace(value) : value;
  }

  metadata() {
    return pathOr('', ['metadata'], this.context);
  }

  payload() {
    const account = getAccountFromStore()
    const currentUserName = pathOr("", ["accountFullName"], account)
    return mkSystemAuditActivity({
      loanapp_id: this.loanAppId,
      entity_id: this.entityId(),
      entity_type: this.entityType(),
      resource: this.resource(),
      action: this.action(),
      actor: returnIsNotEmptyOrTrue(trim(currentUserName), currentUserName, this.actor()),
      status: this.status(),
      message: this.message(),
      metadata: this.metadata(),
    }) as SystemAuditActivity
  };

  queryData() {
    const systemAudit = this.payload()
    const queryData: QueryData = {
      name: 'system_audit_activity_create',
      query: QUERY_CREATE_SYSTEM_ACTIVITIES,
      variables: {
        systemAudits: [systemAudit]
      }
    }
    return queryData
  }
}

class ApplicationAudit implements IAuditActivityContext {
  context: any;
  loanAppId: string;
  loanRoleId: string;
  valueEvaluator: APIResourceValueEvaluator
  constructor(
    context: IContext,
    loanAppId: string,
    loanRoleId: string,
    valueEvaluator: APIResourceValueEvaluator
  ) {
    this.context = context;
    this.loanAppId = loanAppId
    this.loanRoleId = loanRoleId
    this.valueEvaluator = valueEvaluator
  }
  eventActor() {
    const eventActor = pathOr('', ['details', 'event_actor'], this.context)
    return eventActor ? this.valueEvaluator.replace(eventActor) : eventActor;
  }

  action() {
    return pathOr('', ['details', 'action'], this.context);
  }

  purposeOfAction() {
    return pathOr('', ['details', 'purpose_of_action'], this.context);
  }

  metadata() {
    const metadata = isNotEmpty(pathOr({}, ['metadata'], this.context)) ? pathOr({}, ['metadata'], this.context) : pathOr({}, ['details', 'metadata'], this.context) as any
    return map((value: string) => {
      return value ? this.valueEvaluator.replace(value) : value;
    }, metadata)
  }

  linkToUrl() {
    return pathOr('', ['details', 'link_to_url'], this.context);
  }

  eventLog() {
    const value = pathOr('', ['details', 'event_log'], this.context);
    return value ? this.valueEvaluator.replace(value) : value;
  }


  payload() {
    const account = getAccountFromStore()
    const currentUserName = pathOr("", ["accountFullName"], account)
    return mkApplicationActivity({
      loanapp_id: this.loanAppId,
      event_actor: returnIsNotEmptyOrTrue(trim(currentUserName), currentUserName, this.eventActor()),
      action: this.action(),
      purpose_of_action: this.purposeOfAction(),
      event_log: this.eventLog(),
      link_to_url: this.linkToUrl(),
      metadata: this.metadata(),
    }) as ApplicationAuditActivity
  }

  queryData() {
    const applicationAudit = this.payload()
    const queryData: QueryData = {
      name: 'system_audit_activity_create',
      query: QUERY_CREATE_APPLICATION_ACTIVITIES,
      variables: {
        applicationAudits: [applicationAudit]
      }
    }
    return queryData
  }
}

class SystemApplicationAudit implements IAuditActivityContext {
  context: any;
  loanAppId: string;
  loanRoleId: string;
  valueEvaluator: APIResourceValueEvaluator
  constructor(
    context: IContext,
    loanAppId: string,
    loanRoleId: string,
    valueEvaluator: APIResourceValueEvaluator
  ) {
    this.context = context;
    this.loanAppId = loanAppId
    this.loanRoleId = loanRoleId
    this.valueEvaluator = valueEvaluator
  }
  eventActor() {
    const eventActor = pathOr('', ['details', 'event_actor'], this.context)
    return eventActor ? this.valueEvaluator.replace(eventActor) : eventActor;
  }

  action() {
    return pathOr('', ['details', 'action'], this.context);
  }

  purposeOfAction() {
    return pathOr('', ['details', 'purpose_of_action'], this.context);
  }

  metadata() {
    return map((value: string) => {
      return value ? this.valueEvaluator.replace(value) : value;
    }, pathOr({}, ['metadata'], this.context) as any)
  }

  linkToUrl() {
    return pathOr('', ['details', 'link_to_url'], this.context);
  }

  eventLog() {
    const value = pathOr('', ['details', 'event_log'], this.context);
    return value ? this.valueEvaluator.replace(value) : value;
  }

  resource() {
    return pathOr('', ['details', 'resource'], this.context);
  }

  entityId() {
    const value = pathOr('', ['details', 'entity_type'], this.context);
    return value ? this.valueEvaluator.replace(value) : this.loanRoleId;
  }

  entityType() {
    return pathOr('', ['details', 'entity_type'], this.context) || KIND_LOANAPP_ROLE;
  }

  systemAction() {
    return pathOr('', ['details', 'system_action'], this.context);
  }

  actor() {
    const value = pathOr('', ['details', 'actor'], this.context);
    return value ? this.valueEvaluator.replace(value) : value;
  }

  status() {
    return pathOr('', ['details', 'status'], this.context);
  }

  message() {
    const value = pathOr('', ['details', 'message'], this.context);
    return value ? this.valueEvaluator.replace(value) : value;
  }

  systemMetadata() {
    return pathOr('', ['metadata'], this.context);
  }

  systemPayload() {
    const account = getAccountFromStore()
    const currentUserName = pathOr("", ["accountFullName"], account)
    return mkSystemAuditActivity({
      loanapp_id: this.loanAppId,
      entity_id: this.entityId(),
      entity_type: this.entityType(),
      resource: this.resource(),
      action: this.systemAction(),
      actor: returnIsNotEmptyOrTrue(trim(currentUserName), currentUserName, this.actor()),
      status: this.status(),
      message: this.message(),
      metadata: this.systemMetadata(),
    }) as SystemAuditActivity
  }

  applicationPayload() {
    const account = getAccountFromStore()
    const currentUserName = pathOr("", ["accountFullName"], account)
    return mkApplicationActivity({
      loanapp_id: this.loanAppId,
      event_actor: returnIsNotEmptyOrTrue(trim(currentUserName), currentUserName, this.eventActor()),
      action: this.action(),
      purpose_of_action: this.purposeOfAction(),
      event_log: this.eventLog(),
      link_to_url: this.linkToUrl(),
      metadata: this.metadata(),
    }) as ApplicationAuditActivity
  }

  payload() {
    return this.applicationPayload()
  }

  queryData() {
    const systemQueryData: QueryData = {
      name: 'system_audit_activity_create',
      query: QUERY_CREATE_SYSTEM_ACTIVITIES,
      variables: {
        systemAudits: [this.systemPayload()]
      }
    }

    const applicationQueryData: QueryData = {
      name: 'application_audit_activity_create',
      query: QUERY_CREATE_APPLICATION_ACTIVITIES,
      variables: {
        applicationAudits: [this.applicationPayload()]
      }
    }
    return [
      systemQueryData,
      applicationQueryData
    ]
  }
}

class AuditActivityContexts {
  contexts: IContext[];
  loanAppId: string;
  loanRoleId: string;
  loanType: string;
  valueEvaluator: APIResourceValueEvaluator;
  userIntents: any;
  constructor(
    contexts: IContext[],
    loanAppId: string,
    loanType: string,
    loanRoleId: string,
    valueEvaluator: APIResourceValueEvaluator,
    userIntents: any
  ) {
    this.contexts = contexts;
    this.loanAppId = loanAppId
    this.loanRoleId = loanRoleId
    this.loanType = loanType
    this.valueEvaluator = valueEvaluator
    this.userIntents = userIntents
  }

  finder(context: IContext) {
    return cond([
      [equals('system'), always(new SystemAudit(context, this.loanAppId, this.loanRoleId, this.valueEvaluator))],
      [equals('application'), always(new ApplicationAudit(context, this.loanAppId, this.loanRoleId, this.valueEvaluator))],
      [equals('system_application'), always(new SystemApplicationAudit(context, this.loanAppId, this.loanRoleId, this.valueEvaluator))],
      [T, always(new ApplicationAudit(context, this.loanAppId, this.loanRoleId, this.valueEvaluator))]
    ])(pathOr('', ['audit_type'], context))
  }

  queryData(): QueryData[] {
    const contextExecutorQueries = map((context: IContext) => {
      const { shouldRun } = checkDeactivatedUponOnEventContext(context, this.userIntents);
      if (!shouldRun || isEmptyOrNil(context)) {
        return
      }
      const auditClass = this.finder(context);
      const response = auditClass.queryData()
      return response
    }, this.contexts)
    return flatten(
      contextExecutorQueries
    )
  }

  execute() {
    const contextExecutorQueries = this.queryData()
    const queryBuilder = new GQLQueryBuilder(contextExecutorQueries, 'batch_audit_activities', QUERY_OPERATION_MUTATION)
    return requestGraphQL(
      queryBuilder.getQueryString(),
      queryBuilder.getQueryVariables()
    )
  }

}

export const executeAuditActivityRunner = async (
  contexts: IContext[],
  loanAppId: string,
  loanType: string,
  loanRoleId: string,
  valueEvaluator: APIResourceValueEvaluator,
  userIntents?: any
) => {
  const auditContextRunner = new AuditActivityContexts(
    contexts,
    loanAppId,
    loanType,
    loanRoleId,
    valueEvaluator,
    userIntents);
  return auditContextRunner.execute();
};

// Export for test
export {
  SystemAudit as SystemAuditOnlyForTest,
  ApplicationAudit as ApplicationAuditOnlyForTest
}

export {
  AuditActivityContexts,
  SystemAuditData
}